import { useI18n } from "@/i18n/use-i18n";
import { cn } from "@/lib/cn";
import type { DealDetail } from "@/api/types";

/**
 * The deal's live state, always visible above the wizard.
 *
 * Each slot shows a placeholder until it is genuinely filled, so the salesperson
 * can see at a glance what the Vorgang still needs.
 */
export function Ribbon({ deal }: { deal: DealDetail }) {
  const { t } = useI18n();

  const items = [
    { key: "product", value: productValue(deal) },
    { key: "customer", value: deal.customer?.company_name ?? "" },
    {
      key: "documents",
      value: documentsValue(deal, (count, signable) =>
        t("ribbon.documents.value", { count, signable }),
      ),
    },
    { key: "signature", value: signatureValue(deal, t) },
    { key: "payment", value: paymentValue(deal, t) },
  ] as const;

  return (
    <div className="flex overflow-x-auto border-b-[3px] border-teal bg-navy-deep px-4 py-2.5 text-white sm:px-5.5">
      {items.map((item, index) => (
        <div
          key={item.key}
          className={cn(
            // No flex-1 on a phone: five equal columns plus four dividers left
            // ~34px each, so every value truncated to an ellipsis. Sized to
            // content instead, and the strip genuinely scrolls.
            "flex min-w-0 shrink-0 flex-col gap-0.5 sm:shrink sm:flex-1",
            index < items.length - 1 && "mr-3 border-r border-white/15 pr-3 sm:mr-5 sm:pr-5",
          )}
        >
          <span className="text-[10px] font-medium tracking-[0.11em] whitespace-nowrap text-[#8fb4e0] uppercase">
            {t(`ribbon.${item.key}`)}
          </span>
          <span
            className={cn(
              "truncate text-[13.5px] font-medium transition-colors",
              item.value ? "text-white" : "text-white/35",
            )}
          >
            {item.value ? (
              <>
                <span aria-hidden="true" className="align-[2px] text-[9px] text-teal">
                  ●{" "}
                </span>
                {/* Wrapped: bare text beside the dot above is the pattern page
                    translation rewrites, which breaks React's reconciliation. */}
                <span>{item.value}</span>
              </>
            ) : (
              <span>{t(`ribbon.${item.key}.empty`)}</span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

type Translate = ReturnType<typeof useI18n>["t"];

function productValue(deal: DealDetail): string {
  if (!deal.product) return "";
  return `${deal.product.name} · ${deal.product.tag}`;
}

function documentsValue(
  deal: DealDetail,
  label: (count: number, signable: number) => string,
): string {
  if (deal.documents.length === 0) return "";
  const signable = deal.documents.filter((doc) => doc.requires_signature).length;
  return label(deal.documents.length, signable);
}

function signatureValue(deal: DealDetail, t: Translate): string {
  const request = deal.signature_request;
  if (!request) return "";

  const signed = request.signers.filter((signer) => signer.status === "signed").length;
  const total = request.signers.length;

  if (request.status === "completed") return t("ribbon.signature.complete");
  if (signed > 0) return t("ribbon.signature.partial", { signed, total });
  if (request.status === "sent") return t("ribbon.signature.sent", { total });
  return "";
}

function paymentValue(deal: DealDetail, t: Translate): string {
  const payment = deal.payment;
  if (!payment) return "";
  // total_label, not amount_cents: the latter is net, this is the gross the
  // customer paid. German-formatted from the backend.
  if (payment.status === "paid") return t("ribbon.payment.paid", { amount: deal.total_label });
  if (payment.status === "link_sent") return t("ribbon.payment.linkSent");
  if (payment.status === "failed") return t("ribbon.payment.failed");
  return "";
}
